import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Printer, Save, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { ClientPicker } from "@/components/ClientPicker";
import { ProductPicker } from "@/components/ProductPicker";
import { fetchProductImage } from "@/lib/productImages.functions";
import { CO_DEFAULT_TAX_RATE, taxRateForZip, zipFromAddress } from "@/data/salesTax";

export const Route = createFileRoute("/tools/proposals")({
  head: () => ({ meta: [{ title: "Proposals" }, { name: "robots", content: "noindex" }] }),
  component: ProposalsPage,
});

type LineItem = {
  description: string;
  qty: number;
  price: number;
  cost?: number;
  room?: string | null;
  image_url?: string | null;
  product_id?: string | null;
};
type Proposal = {
  id: string;
  title: string;
  client_id: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  client_address: string | null;
  status: string;
  intro: string | null;
  scope: string | null;
  line_items: LineItem[];
  terms: string | null;
  total: number;
  tax_rate: number;
  tax_zip: string | null;
  valid_until: string | null;
  updated_at: string;
};

const empty = (): Proposal => ({
  id: "", title: "New Proposal", client_id: null, client_name: "", client_email: "", client_phone: "", client_address: "",
  status: "draft", intro: "Thank you for the opportunity to provide this proposal for your project.",
  scope: "", line_items: [{ description: "", qty: 1, price: 0, cost: 0, room: "" }],
  terms: "50% deposit required to schedule. Balance due upon completion. Proposal valid for 30 days.",
  total: 0, tax_rate: 0, tax_zip: null, valid_until: null, updated_at: "",
});

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;
const UNASSIGNED = "General";

// Payment-term presets for the Terms & Conditions section.
const PAYMENT_TERMS = [
  { id: "50-50", label: "50% deposit / 50% on completion", text: "Payment terms: A 50% deposit is required to schedule the installation and order equipment. The remaining balance is due upon substantial completion of the work." },
  { id: "40-40-20", label: "40% / 40% / 20% progress billing", text: "Payment terms: 40% deposit due upon acceptance to order equipment and schedule, 40% due at rough-in / midway milestone, and the final 20% due upon substantial completion." },
  { id: "net30", label: "Net 30", text: "Payment terms: Payment in full is due within 30 days of invoice date. Equipment orders may require prepayment before ordering." },
  { id: "full", label: "Payment in full on completion", text: "Payment terms: Payment in full is due upon substantial completion of the work." },
  { id: "custom", label: "Custom (edit text below)", text: "" },
] as const;

const STANDARD_CONDITIONS = [
  "This proposal is valid for 30 days from the date above unless otherwise noted.",
  "Pricing is based on the scope of work described. Changes to the scope may affect pricing and will be documented with a written change order before work proceeds.",
  "Equipment is ordered after receipt of the deposit. Lead times are subject to manufacturer availability.",
  "Apex AV is licensed and insured. Workmanship is warranted for one year from completion; equipment carries the manufacturer's warranty.",
  "Customer is responsible for providing adequate power, network access, and reasonable access to work areas.",
  "Title to equipment passes to the customer upon payment in full.",
];

// Translate raw database errors (e.g. a required column left empty) into plain-language warnings.
const describeSaveError = (message: string): string => {
  const notNull = message.match(/null value in column "([^"]+)"/i);
  if (notNull) {
    const labels: Record<string, string> = {
      title: "Title",
      total: "Total",
      status: "Status",
      line_items: "Line items",
      tax_rate: "Sales tax rate",
    };
    const label = labels[notNull[1]] ?? notNull[1].replace(/_/g, " ");
    return `The "${label}" field is required but was empty. Fill it in and save again.`;
  }
  if (/violates foreign key constraint/i.test(message)) {
    return "The selected client no longer exists — pick the client again and save.";
  }
  if (/duplicate key/i.test(message)) {
    return "A proposal with these details already exists. Change the title or save as an update.";
  }
  return message;
};

function ProposalsPage() {
  const [list, setList] = useState<Proposal[]>([]);
  const [current, setCurrent] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [showImages, setShowImages] = useState(true);
  const [groupByRoom, setGroupByRoom] = useState(true);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [customRoom, setCustomRoom] = useState("");
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [paymentTermId, setPaymentTermId] = useState<string>("50-50");
  const [includeConditions, setIncludeConditions] = useState(true);
  const findProductImage = useServerFn(fetchProductImage);

  const load = async () => {
    const { data } = await supabase.from("proposals").select("*").order("updated_at", { ascending: false });
    setList((data as unknown as Proposal[]) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const subtotal = current ? current.line_items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.price) || 0), 0) : 0;
  const totalCost = current ? current.line_items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.cost) || 0), 0) : 0;
  const profit = subtotal - totalCost;
  const marginPct = subtotal > 0 ? (profit / subtotal) * 100 : 0;
  const taxRate = current ? Number(current.tax_rate) || 0 : 0;
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + tax;

  const rooms = useMemo(() => {
    if (!current) return [] as { room: string; items: { li: LineItem; index: number }[]; subtotal: number; cost: number }[];
    const map = new Map<string, { li: LineItem; index: number }[]>();
    current.line_items.forEach((li, index) => {
      const key = (li.room || "").trim() || UNASSIGNED;
      const arr = map.get(key) ?? [];
      arr.push({ li, index });
      map.set(key, arr);
    });
    return [...map.entries()].map(([room, items]) => ({
      room,
      items,
      subtotal: items.reduce((s, { li }) => s + (Number(li.qty) || 0) * (Number(li.price) || 0), 0),
      cost: items.reduce((s, { li }) => s + (Number(li.qty) || 0) * (Number(li.cost) || 0), 0),
    }));
  }, [current]);

  const roomNames = useMemo(
    () => [...new Set([...list, ...(current ? [current] : [])].flatMap(p => (p.line_items || []).map(li => (li.room || "").trim()).filter(Boolean)))].sort(),
    [list, current],
  );

  const addCustomRoom = () => {
    if (!current) return;
    const room = customRoom.trim();
    if (!room) return toast.error("Enter a room name first.");
    setCurrent({
      ...current,
      line_items: [...current.line_items, { description: "", qty: 1, price: 0, cost: 0, room }],
    });
    setCustomRoom("");
  };

  const applyZipTax = () => {
    if (!current) return;
    const zip = current.tax_zip?.trim() || zipFromAddress(current.client_address);
    const rate = taxRateForZip(zip);
    if (!zip) return toast.error("Add the client address (or a ZIP) first.");
    setCurrent({ ...current, tax_zip: zip, tax_rate: rate ?? CO_DEFAULT_TAX_RATE });
    toast.success(rate ? `${zip}: ${rate}% sales tax applied` : `No rate on file for ${zip} — using ${CO_DEFAULT_TAX_RATE}%`);
  };

  // Build the Terms & Conditions text from the selected payment terms + standard conditions.
  const applyTermsTemplate = (termId: string, withConditions: boolean) => {
    if (!current) return;
    const preset = PAYMENT_TERMS.find((p) => p.id === termId);
    const parts: string[] = [];
    if (preset?.text) parts.push(preset.text);
    if (withConditions) parts.push(...STANDARD_CONDITIONS);
    setCurrent({ ...current, terms: parts.join("\n") });
  };

  const setLine = (index: number, patch: Partial<LineItem>) => {
    if (!current) return;
    const arr = [...current.line_items];
    arr[index] = { ...arr[index], ...patch };
    setCurrent({ ...current, line_items: arr });
  };

  const addCatalogProduct = async (product: import("@/lib/products").Product) => {
    if (!current) return;
    setLoadingPhotos(true);
    let imageUrl = product.image_url;
    if (!imageUrl?.startsWith("data:image/")) {
      try {
        const result = await findProductImage({ data: { productId: product.id } });
        imageUrl = result.image_url;
        if (!imageUrl && result.error) toast.error(result.error);
      } catch (error) {
        toast.error((error as Error).message || "Could not load the product photo");
      }
    }
    setCurrent((proposal) => proposal ? {
      ...proposal,
      line_items: [
        ...proposal.line_items.filter((li) => li.description.trim() || li.price > 0),
        {
          description: [product.description || product.model, product.model && product.description ? `(${product.model})` : ""].filter(Boolean).join(" "),
          qty: 1,
          price: Number(product.sell_price) || 0,
          cost: Number(product.unit_cost) || 0,
          room: proposal.line_items.at(-1)?.room || "",
          image_url: imageUrl || null,
          product_id: product.id,
        },
      ],
    } : proposal);
    setLoadingPhotos(false);
  };

  const refreshProposalPhotos = async () => {
    if (!current) return;
    const targets = current.line_items
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => line.product_id);
    if (targets.length === 0) return toast.info("There are no catalog-linked items to repair.");
    setLoadingPhotos(true);
    const updates = new Map<number, string>();
    for (const { line, index } of targets) {
      if (!line.product_id) continue;
      try {
        const result = await findProductImage({ data: { productId: line.product_id } });
        if (result.image_url) updates.set(index, result.image_url);
      } catch {
        // Continue so one unavailable vendor page does not stop the proposal.
      }
    }
    setCurrent((proposal) => proposal ? {
      ...proposal,
      line_items: proposal.line_items.map((line, index) => ({ ...line, image_url: updates.get(index) ?? line.image_url })),
    } : proposal);
    setLoadingPhotos(false);
    if (updates.size) toast.success(`Added ${updates.size} product photo${updates.size === 1 ? "" : "s"}`);
    else toast.error("No product photos were found for these items.");
  };

  const save = async () => {
    if (!current) return;
    // Validate before hitting the database so missing fields warn the user instead of failing silently.
    const problems: string[] = [];
    const missing: string[] = [];
    if (!current.title.trim()) {
      problems.push("Title is required.");
      missing.push("title");
    }
    if (!current.line_items.some((li) => li.description.trim())) {
      problems.push("Add at least one line item with a description.");
      missing.push("line_items");
    }
    if (current.line_items.some((li) => !(Number(li.qty) > 0))) {
      problems.push("Every line item needs a quantity of at least 1.");
      missing.push("qty");
    }
    if (problems.length) {
      setMissingFields(missing);
      toast.warning("Proposal not saved — missing information", {
        description: problems.join(" "),
        duration: 10000,
      });
      return;
    }
    setMissingFields([]);
    // Strip client-only/managed fields: an empty updated_at string is not a valid timestamp.
    const { id: _id, updated_at: _updatedAt, ...fields } = current;
    const payload = { ...fields, total, line_items: current.line_items };
    if (current.id) {
      const { error } = await supabase.from("proposals").update(payload).eq("id", current.id);
      if (error) {
        return toast.warning("Proposal not saved", {
          description: describeSaveError(error.message),
          duration: 10000,
        });
      }
    } else {
      const { data, error } = await supabase.from("proposals").insert(payload).select().single();
      if (error) {
        return toast.warning("Proposal not saved", {
          description: describeSaveError(error.message),
          duration: 10000,
        });
      }
      setCurrent({ ...((data as unknown) as Proposal), line_items: ((data as unknown) as Proposal).line_items || [] });
    }
    toast.success("Proposal saved successfully", {
      description: `"${current.title.trim()}" is up to date.`,
    });
    load();
  };

  const clearMissing = (field: string) =>
    setMissingFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : prev));


  const del = async (id: string) => {
    if (!confirm("Delete this proposal?")) return;
    await supabase.from("proposals").delete().eq("id", id);
    if (current?.id === id) setCurrent(null);
    load();
  };

  if (current) {
    return (
      <div>
        <div className="mb-4 flex items-center justify-between gap-2 print:hidden">
          <Button variant="ghost" size="sm" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1" /> Print / PDF</Button>
            <Button size="sm" onClick={save}><Save className="h-4 w-4 mr-1" /> Save</Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[420px_1fr] print:block">
          <div className="space-y-4 print:hidden">
            <Field label="Title"><Input className={missingFields.includes("title") ? "border-destructive ring-1 ring-destructive" : ""} value={current.title} onChange={e => { setCurrent({ ...current, title: e.target.value }); clearMissing("title"); }} /></Field>
            <div className="sm:col-span-2"><ClientPicker clientId={current.client_id} clientName={current.client_name} onPick={(f) => setCurrent({ ...current, client_id: f.client_id, client_name: f.client_name, client_email: f.client_email, client_phone: f.client_phone, client_address: f.client_address })} /></div>
            <Field label="Client name"><Input value={current.client_name || ""} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></Field>
            <Field label="Email"><Input value={current.client_email || ""} onChange={e => setCurrent({ ...current, client_email: e.target.value })} /></Field>
            <Field label="Phone"><Input value={current.client_phone || ""} onChange={e => setCurrent({ ...current, client_phone: e.target.value })} /></Field>
            <Field label="Address"><Input value={current.client_address || ""} onChange={e => setCurrent({ ...current, client_address: e.target.value })} /></Field>
            <Field label="Valid until"><Input type="date" value={current.valid_until || ""} onChange={e => setCurrent({ ...current, valid_until: e.target.value || null })} /></Field>
            <Field label="Status">
              <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={current.status} onChange={e => setCurrent({ ...current, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
              </select>
            </Field>

            {/* Sales tax */}
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-2">Sales tax</div>
              <div className="grid grid-cols-[1fr_1fr] gap-2">
                <Field label="Tax ZIP">
                  <Input
                    placeholder="80202"
                    value={current.tax_zip || ""}
                    onChange={e => setCurrent({ ...current, tax_zip: e.target.value || null })}
                  />
                </Field>
                <Field label="Rate %">
                  <NumberInput value={taxRate} onValueChange={n => setCurrent({ ...current, tax_rate: n })} />
                </Field>
              </div>
              <Button className="mt-2" variant="outline" size="sm" onClick={applyZipTax}>
                Look up rate from ZIP / address
              </Button>
            </div>

            {/* Profit */}
            <div className="rounded-lg border border-border p-3 text-sm">
              <div className="text-xs font-medium text-muted-foreground mb-2">Estimated profit (internal only)</div>
              <Row label="Sell subtotal" value={money(subtotal)} />
              <Row label="Product / labor cost" value={money(totalCost)} />
              <Row label="Gross profit" value={money(profit)} strong />
              <Row label="Margin" value={`${marginPct.toFixed(1)}%`} />
              <Row label={`Sales tax (${taxRate}%)`} value={money(tax)} />
              <Row label="Client total" value={money(total)} strong />
            </div>

            <Field label="Intro"><Textarea rows={3} value={current.intro || ""} onChange={e => setCurrent({ ...current, intro: e.target.value })} /></Field>
            <Field label="Scope of work"><Textarea rows={5} value={current.scope || ""} onChange={e => setCurrent({ ...current, scope: e.target.value })} /></Field>
            {/* Terms & Conditions */}
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs font-medium text-muted-foreground mb-2">Terms &amp; Conditions</div>
              <Field label="Payment terms">
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={paymentTermId}
                  onChange={(e) => setPaymentTermId(e.target.value)}
                >
                  {PAYMENT_TERMS.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </Field>
              <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={includeConditions} onChange={(e) => setIncludeConditions(e.target.checked)} />
                Include standard conditions (validity, change orders, warranty, etc.)
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => applyTermsTemplate(paymentTermId, includeConditions)}
              >
                Apply to terms text
              </Button>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Fills the text below with the selected payment terms — you can still edit it freely.
              </p>
              <Textarea
                className="mt-2"
                rows={8}
                value={current.terms || ""}
                onChange={e => setCurrent({ ...current, terms: e.target.value })}
              />
            </div>

            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Line items</div>
              <div className="mb-3 flex gap-2">
                <Input
                  placeholder="Add a custom room name"
                  value={customRoom}
                  onChange={(e) => setCustomRoom(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomRoom(); } }}
                />
                <Button type="button" variant="outline" onClick={addCustomRoom}>
                  <Plus className="mr-1 h-4 w-4" /> Add room
                </Button>
              </div>
              <div className="mb-1 grid grid-cols-[1fr_54px_74px_74px_auto] gap-1 text-[10px] uppercase text-muted-foreground">
                <span>Description / room</span><span>Qty</span><span>Cost</span><span>Sell</span><span />
              </div>
              {current.line_items.map((li, i) => (
                <div key={i} className="mb-3 grid grid-cols-[1fr_54px_74px_74px_auto] gap-1">
                  <Input placeholder="Description" className={missingFields.includes("line_items") ? "border-destructive ring-1 ring-destructive" : ""} value={li.description} onChange={e => { setLine(i, { description: e.target.value }); clearMissing("line_items"); }} />
                  <NumberInput value={li.qty} onValueChange={n => setLine(i, { qty: n })} />
                  <NumberInput value={li.cost ?? 0} onValueChange={n => setLine(i, { cost: n })} />
                  <NumberInput value={li.price} onValueChange={n => setLine(i, { price: n })} />
                  <Button variant="ghost" size="icon" className="col-start-5 row-start-1" onClick={() => setCurrent({ ...current, line_items: current.line_items.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <div className="col-span-4 col-start-1 flex items-center gap-2">
                    <Input
                      list="proposal-rooms"
                      placeholder="Room (e.g. Great Room)"
                      className="h-8 text-xs"
                      value={li.room || ""}
                      onChange={e => setLine(i, { room: e.target.value })}
                    />
                    {li.image_url ? (
                      <button
                        type="button"
                        title="Remove photo from this line"
                        onClick={() => setLine(i, { image_url: null })}
                        className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <img src={li.image_url} alt="" className="h-8 w-8 rounded border border-border object-contain bg-white" />
                        Remove
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
              <datalist id="proposal-rooms">
                {roomNames.map(r => <option key={r} value={r} />)}
                {["Great Room", "Media Room", "Primary Bedroom", "Kitchen", "Patio", "Rack / Head End", "Whole Home"].map(r => <option key={r} value={r} />)}
              </datalist>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrent({ ...current, line_items: [...current.line_items, { description: "", qty: 1, price: 0, cost: 0, room: current.line_items.at(-1)?.room || "" }] })}>
                  <Plus className="h-4 w-4 mr-1" /> Add line
                </Button>
                <ProductPicker onPick={addCatalogProduct} />
                <Button type="button" variant="outline" size="sm" disabled={loadingPhotos} onClick={refreshProposalPhotos}>
                  <ImageIcon className="mr-1 h-4 w-4" /> {loadingPhotos ? "Loading photos…" : "Repair / load photos"}
                </Button>
              </div>
              <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={showImages} onChange={(e) => setShowImages(e.target.checked)} />
                Show product photos on the printed proposal
              </label>
              <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={groupByRoom} onChange={(e) => setGroupByRoom(e.target.checked)} />
                Break the printed proposal down by room
              </label>
            </div>
          </div>

          {/* Print preview */}
          <div className="rounded-lg border border-border bg-white p-8 text-black print:border-0 print:p-0">
            <div className="flex justify-between items-start mb-6 border-b pb-4">
              <div>
                <h1 className="text-2xl font-bold">Apex AV</h1>
                <p className="text-sm text-gray-600">Denver, CO · 303-317-3233 · apexav.com</p>
              </div>
              <div className="text-right">
                <div className="text-xl font-semibold">PROPOSAL</div>
                <div className="text-sm text-gray-600">{new Date().toLocaleDateString()}</div>
                {current.valid_until && <div className="text-xs text-gray-500">Valid until {current.valid_until}</div>}
              </div>
            </div>
            <div className="mb-4">
              <div className="text-xs uppercase text-gray-500 mb-1">Prepared for</div>
              <div className="font-semibold">{current.client_name}</div>
              <div className="text-sm whitespace-pre-line">{current.client_address}</div>
              <div className="text-sm">{current.client_email} {current.client_phone && `· ${current.client_phone}`}</div>
            </div>
            <h2 className="text-lg font-semibold mt-6 mb-2">{current.title}</h2>
            {current.intro && <p className="text-sm whitespace-pre-line mb-4">{current.intro}</p>}
            {current.scope && (<><h3 className="font-semibold mt-4 mb-1">Scope of Work</h3><p className="text-sm whitespace-pre-line mb-4">{current.scope}</p></>)}

            <table className="w-full text-sm my-4">
              <thead className="bg-gray-100"><tr><th className="text-left p-2">Description</th><th className="text-right p-2 w-16">Qty</th><th className="text-right p-2 w-24">Price</th><th className="text-right p-2 w-24">Total</th></tr></thead>
              <tbody>
                {groupByRoom
                  ? rooms.map((group) => (
                      <Fragment key={group.room}>
                        <tr className="bg-gray-50">
                          <td colSpan={4} className="p-2 text-xs font-semibold uppercase tracking-wide text-gray-700">{group.room}</td>
                        </tr>
                        {group.items.map(({ li, index }) => (
                          <PrintRow key={index} li={li} showImages={showImages} />
                        ))}
                        <tr className="border-b">
                          <td colSpan={3} className="p-2 text-right text-xs italic text-gray-600">{group.room} subtotal</td>
                          <td className="p-2 text-right text-xs font-semibold">{money(group.subtotal)}</td>
                        </tr>
                      </Fragment>
                    ))
                  : current.line_items.map((li, i) => <PrintRow key={i} li={li} showImages={showImages} />)}
              </tbody>
              <tfoot>
                <tr><td colSpan={3} className="text-right p-2">Subtotal</td><td className="text-right p-2">{money(subtotal)}</td></tr>
                {taxRate > 0 && (
                  <tr>
                    <td colSpan={3} className="text-right p-2">Sales tax ({taxRate}%{current.tax_zip ? ` · ${current.tax_zip}` : ""})</td>
                    <td className="text-right p-2">{money(tax)}</td>
                  </tr>
                )}
                <tr className="font-bold"><td colSpan={3} className="text-right p-2">Total</td><td className="text-right p-2">{money(total)}</td></tr>
              </tfoot>
            </table>

            {current.terms && (<><h3 className="font-semibold mt-6 mb-1">Terms &amp; Conditions</h3><p className="text-xs whitespace-pre-line text-gray-700">{current.terms}</p></>)}
            <div className="mt-12 grid grid-cols-2 gap-8">
              <div><div className="border-b border-black h-8"></div><div className="text-xs text-gray-500 mt-1">Client signature / date</div></div>
              <div><div className="border-b border-black h-8"></div><div className="text-xs text-gray-500 mt-1">Apex AV representative</div></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Proposals</h1>
          <p className="text-sm text-muted-foreground mt-1">Room-by-room proposals with profit and sales tax.</p>
        </div>
        <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" /> New proposal</Button>
      </div>
      {loading ? <div className="text-muted-foreground">Loading…</div> : list.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">No proposals yet.</div>
      ) : (
        <div className="rounded-lg border border-border divide-y divide-border">
          {list.map(p => {
            const items = (p.line_items as unknown as LineItem[]) || [];
            const sell = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.price) || 0), 0);
            const cost = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.cost) || 0), 0);
            return (
              <div key={p.id} className="flex items-center justify-between p-4 hover:bg-accent">
                <button onClick={() => setCurrent({ ...p, tax_rate: Number(p.tax_rate) || 0, line_items: items })} className="flex-1 text-left">
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.client_name || "No client"} · {money(Number(p.total))} · {p.status}
                    {sell > 0 && <> · profit {money(sell - cost)}</>}
                  </div>
                </button>
                <Button variant="ghost" size="icon" onClick={() => del(p.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PrintRow({ li, showImages }: { li: LineItem; showImages: boolean }) {
  return (
    <tr className="border-b">
      <td className="p-2">
        <div className="flex items-center gap-3">
          {showImages && li.image_url ? (
            <img src={li.image_url} alt={li.description} className="h-14 w-14 shrink-0 rounded border border-gray-200 object-contain bg-white" />
          ) : null}
          <span>{li.description}</span>
        </div>
      </td>
      <td className="text-right p-2">{li.qty}</td>
      <td className="text-right p-2">{money(Number(li.price))}</td>
      <td className="text-right p-2">{money((Number(li.qty) || 0) * (Number(li.price) || 0))}</td>
    </tr>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between ${strong ? "font-semibold" : "text-muted-foreground"}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (<label className="block"><div className="text-xs font-medium text-muted-foreground mb-1">{label}</div>{children}</label>);
}
